import { LitElement, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { expandTextStyles } from "./expand-text.css";

@customElement("expand-text")
export class ExpandText extends LitElement {
  @property({ type: Boolean }) expanded = false;
  @property({ type: Number }) lineClamp = 3;
  @property({ type: Boolean, reflect: true, attribute: "no-more" }) noMore = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @state() private _isOverflowing = false;

  static override styles = [expandTextStyles];

  protected override firstUpdated(): void {
    this._checkOverflow();
    this._setMaxHeight();
  }

  protected override updated(changedProps: PropertyValues<this>): void {
    if (changedProps.has("expanded")) {
      this._setMaxHeight();
    }
  }

  private _checkOverflow(): void {
    const text = this.renderRoot.querySelector<HTMLElement>(".text");
    if (!text) return;

    const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
    const collapsedHeight = lineHeight * this.lineClamp;

    requestAnimationFrame(() => {
      this._isOverflowing = text.scrollHeight > collapsedHeight;
    });
  }

  private _setMaxHeight(): void {
    const wrapper = this.renderRoot.querySelector<HTMLElement>(".wrapper");
    const text = this.renderRoot.querySelector<HTMLElement>(".text");
    if (!wrapper || !text) return;

    const fullHeight = text.scrollHeight;

    if (this.expanded) {
      wrapper.style.maxHeight = `${fullHeight}px`;
    } else {
      const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
      const collapsedHeight = lineHeight * this.lineClamp;
      wrapper.style.maxHeight = `${collapsedHeight}px`;
    }
  }

  private toggle(): void {
    if (this.disabled) return;
    this.expanded = !this.expanded;
  }

  protected override render() {
    return html`
      <div class="wrapper">
        <div
          class="text"
          style=${!this.expanded ? `-webkit-line-clamp: ${this.lineClamp};` : ""}
          @click=${this.noMore && this._isOverflowing ? this.toggle : undefined}
        >
          <slot @slotchange=${() => this._checkOverflow()}></slot>
        </div>
      </div>
      ${!this.noMore && this._isOverflowing
        ? html`<span class="toggle" @click=${this.toggle}> ${this.expanded ? "less" : "more"} </span>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "expand-text": ExpandText;
  }
}
